<template>
  <div class="documents-table-container">
    <!-- Search and Filter Controls -->
    <div class="table-controls" v-if="documents && documents.length > 0">
      <div class="search-container">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search documents..." 
          class="search-input"
        >
        <i class="fas fa-search search-icon"></i>
        <button 
          v-if="searchQuery" 
          @click="clearSearch" 
          class="clear-search-btn"
          title="Clear search"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="filter-container" v-if="hasDocumentTypes">
        <select v-model="typeFilter" class="type-filter">
          <option value="">All document types</option>
          <option v-for="type in documentTypes" :key="type" :value="type">
            {{ formatDocumentType(type) }}
          </option>
        </select>
      </div>
    </div>
  
    <!-- Table View for Documents -->
    <table class="documents-table" v-if="filteredDocuments.length > 0">
      <thead>
        <tr>
          <th @click="sortDocuments('name')" class="sortable-header">
            <div class="header-content">
              <i class="fas fa-file-alt header-icon"></i>
              <span>Name</span>
              <i v-if="sortConfig.field === 'name'" 
                :class="['fas', sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'sort-active']">
              </i>
              <i v-else class="fas fa-sort sort-icon"></i>
            </div>
          </th>
          <th @click="sortDocuments('document_type')" class="sortable-header">
            <div class="header-content">
              <i class="fas fa-tag header-icon"></i>
              <span>Type</span>
              <i v-if="sortConfig.field === 'document_type'" 
                :class="['fas', sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'sort-active']">
              </i>
              <i v-else class="fas fa-sort sort-icon"></i>
            </div>
          </th>
          <th @click="sortDocuments('created_at')" class="sortable-header">
            <div class="header-content">
              <i class="fas fa-calendar-alt header-icon"></i>
              <span>Uploaded Date</span>
              <i v-if="sortConfig.field === 'created_at'" 
                :class="['fas', sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'sort-active']">
              </i>
              <i v-else class="fas fa-sort sort-icon"></i>
            </div>
          </th>
          <th @click="sortDocuments('uploaded_by_name')" class="sortable-header">
            <div class="header-content">
              <i class="fas fa-user header-icon"></i>
              <span>Uploaded By</span>
              <i v-if="sortConfig.field === 'uploaded_by_name'" 
                :class="['fas', sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'sort-active']">
              </i>
              <i v-else class="fas fa-sort sort-icon"></i>
            </div>
          </th>
          <th @click="sortDocuments('upload_method')" class="sortable-header">
            <div class="header-content">
              <i class="fas fa-upload header-icon"></i>
              <span>Upload Method</span>
              <i v-if="sortConfig.field === 'upload_method'" 
                :class="['fas', sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'sort-active']">
              </i>
              <i v-else class="fas fa-sort sort-icon"></i>
            </div>
          </th>
          <th>
            <div class="header-content">
              <i class="fas fa-file-alt header-icon"></i>
              <span>Pages</span>
            </div>
          </th>
          <th>
            <div class="header-content">
              <i class="fas fa-cog header-icon"></i>
              <span>Actions</span>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="doc in paginatedDocuments" :key="doc.id" class="document-row">
          <td class="document-name">
            <div class="document-name-container">
              <i :class="getDocumentIcon(doc.document_type)"></i>
              <span>{{ doc.name || 'Untitled Document' }}</span>
            </div>
          </td>
          <td class="document-type">
            <span class="type-badge">{{ formatDocumentType(doc.document_type) || 'Unknown Type' }}</span>
          </td>
          <td class="upload-date">{{ formatDate(doc.created_at) }}</td>
          <td class="uploader">{{ doc.uploaded_by_name || doc.uploaded_by_email || 'Unknown' }}</td>
          <td class="upload-method">{{ formatUploadMethod(doc.upload_method) }}</td>
          <!-- Pages / Tier column -->
          <td class="document-pages">
            <div class="pages-cell">
              <span v-if="doc.page_count" class="page-count">{{ doc.page_count }}p</span>
              <span v-else class="page-count page-count--unknown">—</span>
              <span
                v-if="getTierLabel(doc)"
                :class="['tier-pill', getTierLabel(doc).cls]"
                :title="getTierLabel(doc).title"
              >{{ getTierLabel(doc).label }}</span>
              <i
                v-if="getChunkStatusIcon(doc)"
                :class="['chunk-status-dot', getChunkStatusIcon(doc).icon, getChunkStatusIcon(doc).cls]"
                :title="getChunkStatusIcon(doc).title"
              ></i>
            </div>
          </td>

          <td class="document-actions">
            <div class="action-buttons">
              <a :href="getDownloadUrl(doc)" class="action-button download" v-if="doc.file" title="Download">
                <i class="fas fa-download"></i>
                <span class="action-label">Download</span>
                <span class="action-tooltip">Download</span>
              </a>
              <button @click="viewDocument(doc)" class="action-button view" title="View">
                <i class="fas fa-eye"></i>
                <span class="action-label">View</span>
                <span class="action-tooltip">View</span>
              </button>
              <button
                v-if="doc.page_count > 20"
                @click="openChunkingPanel(doc)"
                class="action-button chunk"
                :title="doc.chunking_status === 'complete' ? 'View chunks' : 'Chunk document'"
              >
                <i class="fas fa-layer-group"></i>
                <span class="action-label">{{ doc.chunking_status === 'complete' ? 'Chunks' : 'Chunk' }}</span>
                <span class="action-tooltip">{{ doc.chunking_status === 'complete' ? 'View chunks' : 'Chunk document' }}</span>
              </button>
              <button @click="deleteDocument(doc.id)" class="action-button delete" title="Delete">
                <i class="fas fa-trash"></i>
                <span class="action-label">Delete</span>
                <span class="action-tooltip">Delete</span>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    
    <!-- Chunking Panel Modal -->
    <div v-if="chunkingDocId" class="chunking-modal-overlay" @click.self="closeChunkingPanel">
      <div class="chunking-modal">
        <DocumentChunkingPanel
          :document-id="chunkingDocId"
          @close="closeChunkingPanel"
          @chunking-complete="closeChunkingPanel"
        />
      </div>
    </div>

    <!-- No Results State -->
    <div v-if="!loading && documents.length > 0 && filteredDocuments.length === 0" class="no-results">
      <i class="fas fa-search"></i>
      <p>No documents match your search criteria</p>
      <button @click="resetFilters" class="reset-filters-btn">
        <i class="fas fa-times-circle"></i> Reset Filters
      </button>
    </div>
    
    <!-- Pagination -->
    <div class="pagination-container" v-if="filteredDocuments.length > 0">
      <div class="pagination-info">
        Showing {{ displayedRange }} of {{ filteredDocuments.length }} documents
        <span v-if="documents.length !== filteredDocuments.length">
          (filtered from {{ documents.length }} total)
        </span>
      </div>
      <div class="pagination-controls">
        <button 
          @click="goToPage(currentPage - 1)" 
          class="pagination-button"
          :disabled="currentPage === 1"
          title="Previous Page"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        
        <div class="pagination-pages">
          <button 
            v-for="page in displayedPages" 
            :key="page" 
            @click="goToPage(page)"
            :class="['page-number', { active: currentPage === page }]"
          >
            {{ page }}
          </button>
        </div>
        
        <button 
          @click="goToPage(currentPage + 1)" 
          class="pagination-button"
          :disabled="currentPage === totalPages"
          title="Next Page"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
      
      <div class="items-per-page">
        <label for="items-per-page">Items per page:</label>
        <select id="items-per-page" v-model.number="itemsPerPage">
          <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <span>Loading documents...</span>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && (!documents || documents.length === 0)" class="empty-state">
      <i class="fas fa-file-alt"></i>
      <p>No documents uploaded yet</p>
      <p class="empty-state-hint">Upload documents from the "Upload Documents" tab</p>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { errorHandlingMixin } from '@/mixins/errorHandling';
import DocumentChunkingPanel from '@/components/DocumentChunkingPanel.vue';

export default {
  name: 'DocumentList',

  components: { DocumentChunkingPanel },
  
  props: {
    documents: {
      type: Array,
      required: true,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  mixins: [errorHandlingMixin],
  
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 10,
      itemsPerPageOptions: [5, 10, 25, 50, 100],
      sortConfig: {
        field: 'created_at',
        direction: 'desc'
      },
      searchQuery: '',
      typeFilter: '',
      sortedDocuments: [],
      chunkingDocId: null,
    };
  },
  
  computed: {
    filteredDocuments() {
      if (!this.documents.length) return [];
      
      // First, filter by search and type
      let filtered = this.documents;

      // Apply type filter
      if (this.typeFilter) {
        filtered = filtered.filter(doc => 
          doc.document_type === this.typeFilter
        );
      }
      
      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(doc => 
          (doc.name && doc.name.toLowerCase().includes(query)) ||
          (doc.document_type && doc.document_type.toLowerCase().includes(query)) ||
          (doc.uploaded_by_name && doc.uploaded_by_name.toLowerCase().includes(query)) ||
          (doc.uploaded_by_email && doc.uploaded_by_email.toLowerCase().includes(query)) ||
          (doc.upload_notes && doc.upload_notes.toLowerCase().includes(query))
        );
      }
      
      // Then sort
      return this.sortDocumentsByConfig([...filtered]);
    },
    
    documentTypes() {
      // Extract unique document types from all documents
      const types = new Set();
      this.documents.forEach(doc => {
        if (doc.document_type) {
          types.add(doc.document_type);
        }
      });
      return Array.from(types).sort();
    },
    
    hasDocumentTypes() {
      return this.documentTypes.length > 0;
    },
    
    hasActiveFilters() {
      return this.searchQuery.trim() !== '' || this.typeFilter !== '';
    },
    
    totalPages() {
      return Math.ceil(this.filteredDocuments.length / this.itemsPerPage);
    },
    
    paginatedDocuments() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredDocuments.slice(start, end);
    },
    
    displayedRange() {
      if (this.filteredDocuments.length === 0) return '0-0';
      const start = (this.currentPage - 1) * this.itemsPerPage + 1;
      const end = Math.min(start + this.itemsPerPage - 1, this.filteredDocuments.length);
      return `${start}-${end}`;
    },
    
    displayedPages() {
      const pages = [];
      const maxVisiblePages = 5;
      
      if (this.totalPages <= maxVisiblePages) {
        // Show all pages if we have fewer than maxVisiblePages
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show a subset of pages centered around current page
        let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = startPage + maxVisiblePages - 1;
        
        // Adjust if we hit the right boundary
        if (endPage > this.totalPages) {
          endPage = this.totalPages;
          startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }
      }
      
      return pages;
    }
  },
  
  watch: {
    documents: {
      immediate: true,
      handler() {
        // Reset to first page when document list changes
        this.currentPage = 1;
      }
    },
    
    searchQuery() {
      // Reset to first page when search changes
      this.currentPage = 1;
    },
    
    typeFilter() {
      // Reset to first page when filter changes
      this.currentPage = 1;
    },
    
    itemsPerPage() {
      // Reset to first page when items per page changes
      this.currentPage = 1;
    }
  },
  
  methods: {
    resetFilters() {
      this.searchQuery = '';
      this.typeFilter = '';
      this.currentPage = 1;
    },
    
    clearSearch() {
      this.searchQuery = '';
    },
    
    sortDocumentsByConfig(docs) {
      const { field, direction } = this.sortConfig;
      
      return docs.sort((a, b) => {
        let aValue = a[field];
        let bValue = b[field];
        
        // Handle dates
        if (field === 'created_at') {
          aValue = new Date(aValue || 0).getTime();
          bValue = new Date(bValue || 0).getTime();
        } 
        // Handle strings
        else if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        // Handle nulls and undefined
        else {
          aValue = aValue || '';
          bValue = bValue || '';
        }
        
        if (direction === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
    },
    
    sortDocuments(field) {
      // Toggle direction if the same field is clicked
      if (this.sortConfig.field === field) {
        this.sortConfig.direction = this.sortConfig.direction === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortConfig.field = field;
        this.sortConfig.direction = 'asc';
      }
      
      // Reset to first page
      this.currentPage = 1;
    },

    formatDate(date) {
      if (!date) return 'Unknown date';
      try {
        return new Date(date).toLocaleDateString();
      } catch (e) {
        return 'Invalid date';
      }
    },

    formatUploadMethod(method) {
      if (!method) return 'Manual upload';
      try {
        return method.replace(/_/g, ' ').toLowerCase();
      } catch (e) {
        return 'Manual upload';
      }
    },
    
    formatDocumentType(type) {
      if (!type) return 'Unknown Type';
      return type.replace(/_/g, ' ');
    },

    getDocumentUrl(doc) {
      // Use unified document URL helper
      const { getDocumentUrl } = require('@/utils/documentUrlHelper');
      return getDocumentUrl(doc);
    },
    
    getDownloadUrl(doc) {
      // Use unified download URL helper
      const { getDocumentDownloadUrl } = require('@/utils/documentUrlHelper');
      return getDocumentDownloadUrl(doc);
    },
    
    getDocumentIcon(type) {
      if (!type) return 'fas fa-file';
      
      const lowerType = type.toLowerCase();
      
      if (lowerType.includes('pdf')) return 'fas fa-file-pdf';
      if (lowerType.includes('word') || lowerType.includes('doc')) return 'fas fa-file-word';
      if (lowerType.includes('excel') || lowerType.includes('spreadsheet') || lowerType.includes('xls')) return 'fas fa-file-excel';
      if (lowerType.includes('image') || lowerType.includes('photo')) return 'fas fa-file-image';
      if (lowerType.includes('contract')) return 'fas fa-file-signature';
      if (lowerType.includes('invoice')) return 'fas fa-file-invoice-dollar';
      if (lowerType.includes('insurance')) return 'fas fa-shield-alt';
      
      return 'fas fa-file-alt';
    },
    
    viewDocument(doc) {
      if (doc.file) {
        window.open(this.getDocumentUrl(doc), '_blank');
      }
    },

    async deleteDocument(documentId) {
      if (confirm('Are you sure you want to delete this document?')) {
        try {
          await api.delete(`/projects/${this.$route.params.id}/documents/${documentId}/`);
          this.$emit('document-deleted', documentId);
          if (this.$toast) {
            this.$toast.success('Document deleted successfully');
          }
        } catch (error) {
          this.handleError(error, 'Failed to delete document');
        }
      }
    },
    
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    openChunkingPanel(doc) {
      this.chunkingDocId = doc.id;
    },

    closeChunkingPanel() {
      this.chunkingDocId = null;
    },

    getTierLabel(doc) {
      if (!doc.page_count) return null;
      if (doc.page_count <= 20)  return { label: 'T1', title: 'Tier 1 — Full Doc (1–20 pages)', cls: 'tier-t1' };
      if (doc.page_count <= 60)  return { label: 'T2', title: 'Tier 2 — Chunked (21–60 pages)', cls: 'tier-t2' };
      return { label: 'T3', title: 'Tier 3 — Hierarchical (60+ pages)', cls: 'tier-t3' };
    },

    getChunkStatusIcon(doc) {
      const s = doc.chunking_status;
      if (s === 'complete')    return { icon: 'fas fa-check-circle', cls: 'chunk-ok',  title: 'Chunking complete' };
      if (s === 'processing')  return { icon: 'fas fa-spinner fa-spin', cls: 'chunk-running', title: 'Chunking in progress…' };
      if (s === 'failed')      return { icon: 'fas fa-exclamation-circle', cls: 'chunk-err', title: 'Chunking failed' };
      return null;
    },
  }
};
</script>

<style scoped>
.documents-table-container {
  width: 100%;
  overflow-x: auto;
  position: relative;
  margin-top: 10px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Table Controls */
.table-controls {
  display: flex;
  justify-content: space-between;
  padding: 15px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 10px;
}

.search-container {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.search-input {
  width: 100%;
  padding: 8px 36px 8px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
}

.clear-search-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 2px;
  font-size: 0.875rem;
}

.clear-search-btn:hover {
  color: #4a5568;
}

.filter-container {
  min-width: 150px;
}

.type-filter {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #4a5568;
  background: white;
  min-width: 180px;
  max-width: 300px;
}

.type-filter:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

/* No Results State */
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #a0aec0;
  text-align: center;
}

.no-results i {
  font-size: 2.5rem;
  margin-bottom: 15px;
  opacity: 0.5;
}

.no-results p {
  margin: 0;
  margin-bottom: 15px;
  font-size: 1rem;
  color: #718096;
}

.reset-filters-btn {
  background: #e2e8f0;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  color: #4a5568;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.reset-filters-btn:hover {
  background: #cbd5e0;
  color: #2d3748;
}

.reset-filters-btn i {
  font-size: 0.875rem;
  opacity: 1;
  margin: 0;
}

.documents-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  color: #2d3748;
}

.documents-table thead {
  background: #edf2f7;
  border-bottom: 2px solid #cbd5e0;
}

.documents-table th {
  padding: 12px 15px;
  text-align: left;
  font-weight: 600;
  color: #2d3748;
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: #edf2f7;
  z-index: 1;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 5px;
}

.header-icon {
  color: #4a5568;
  font-size: 0.875rem;
}

.sort-icon {
  opacity: 0.3;
  font-size: 0.75rem;
  margin-left: auto;
}

.sort-active {
  opacity: 1;
  color: #3182ce;
  margin-left: auto;
}

.sortable-header:hover {
  background-color: #e2e8f0;
}

.documents-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: background-color 0.2s ease;
}

.documents-table tbody tr:hover {
  background-color: #f7fafc;
}

.documents-table td {
  padding: 12px 15px;
  vertical-align: middle;
}

.document-name-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.document-name-container i {
  color: #4a5568;
  font-size: 1rem;
  width: 16px;
}

.document-name {
  font-weight: 500;
  color: #2d3748;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.type-badge {
  display: inline-block;
  padding: 4px 8px;
  background: #edf2f7;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #4a5568;
  white-space: nowrap;
}

.upload-date, .uploader, .upload-method {
  color: #718096;
  white-space: nowrap;
}

.document-actions {
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 6px;
  align-items: center;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #4a5568;
  transition: all 0.2s;
  cursor: pointer;
  position: relative;
  font-size: 0.75rem;
  min-width: 28px;
  height: 28px;
}

.action-label {
  display: none;
}

.action-button.download {
  color: #3182ce;
  background-color: #ebf8ff;
  border-color: #bee3f8;
}

.action-button.view {
  color: #38a169;
  background-color: #f0fff4;
  border-color: #c6f6d5;
}

.action-button.delete {
  color: #e53e3e;
  background-color: #fff5f5;
  border-color: #fed7d7;
}

/* Only show text label on larger screens */
@media (min-width: 992px) {
  .action-button {
    padding: 4px 8px;
    min-width: auto;
  }
  
  .action-label {
    display: inline;
  }
  
  .action-buttons {
    gap: 8px;
  }
}

.action-button:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.action-button:hover .action-tooltip {
  visibility: visible;
  opacity: 1;
}

.action-tooltip {
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: #2d3748;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  visibility: hidden;
  opacity: 0;
  transition: all 0.2s;
  z-index: 10;
  pointer-events: none;
}

.action-tooltip::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent #2d3748 transparent;
}

.loading-state {
  padding: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3182ce;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 20px;
  color: #a0aec0;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 15px;
}

.empty-state p {
  margin: 0;
  margin-bottom: 5px;
  font-size: 1rem;
}

.empty-state-hint {
  font-size: 0.875rem;
  color: #718096;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 15px;
}

.pagination-info {
  font-size: 0.875rem;
  color: #718096;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pagination-button {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-pages {
  display: flex;
  gap: 5px;
}

.page-number {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
}

.page-number:hover:not(.active) {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.page-number.active {
  background: #3182ce;
  color: white;
  border-color: #3182ce;
  font-weight: 600;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #718096;
}

.items-per-page select {
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.875rem;
  color: #4a5568;
  background: white;
}

/* Responsive styling */
@media (max-width: 768px) {
  .documents-table {
    font-size: 0.75rem;
  }
  
  .documents-table th, 
  .documents-table td {
    padding: 8px 10px;
  }
  
  .document-name {
    max-width: 150px;
  }
  
  .pagination-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .pagination-controls {
    order: 1;
    justify-content: center;
  }
  
  .pagination-info {
    order: 2;
    text-align: center;
  }
  
  .items-per-page {
    order: 3;
    justify-content: center;
  }
  
  .table-controls {
    flex-direction: column;
  }
}

/* ── Pages / Tier column ─────────────────────────────── */
.document-pages { white-space: nowrap; }

.pages-cell {
  display: flex;
  align-items: center;
  gap: 5px;
}
.page-count {
  font-size: 0.8rem;
  color: #4a5568;
  font-variant-numeric: tabular-nums;
}
.page-count--unknown { color: #a0aec0; }

.tier-pill {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  cursor: default;
  letter-spacing: 0.02em;
}
.tier-t1 { background: #edf2f7; color: #718096; }
.tier-t2 { background: #ebf4ff; color: #3182ce; }
.tier-t3 { background: #fefcbf; color: #b7791f; }

.chunk-status-dot { font-size: 0.75rem; }
.chunk-ok      { color: #38a169; }
.chunk-running { color: #3182ce; }
.chunk-err     { color: #e53e3e; }

/* Chunk action button */
.action-button.chunk {
  background: #ebf4ff;
  color: #3182ce;
  border: 1px solid #bee3f8;
}
.action-button.chunk:hover {
  background: #bee3f8;
  color: #2b6cb0;
}

/* ── Chunking panel modal ────────────────────────────── */
.chunking-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.chunking-modal {
  width: 100%;
  max-width: 680px;
  max-height: 85vh;
  overflow-y: auto;
  border-radius: 12px;
}
</style> 